import React, { useEffect, useState } from "react";
import { useAuth } from "@hooks/useAuth";
import { useFeedback } from "@contexts/FeedbackContext";
import {
  usuarioService,
  type PerfilUsuario,
  type UsuarioSistema,
} from "@services/usuarioService";
import { formatarTempoRelativo } from "@utils/tempoRelativo";
import "../pages-common.css";

const DESCRICAO_PERFIL: Record<PerfilUsuario, string> = {
  Admin: "Faz tudo e decide o perfil de cada usuário",
  Editor: "Faz tudo no sistema, menos gerenciar usuários e permissões",
  Visualizador: "Só consulta: planilhas, clientes, fornecedores e exportações",
};

/**
 * Usuários da equipe e o perfil de cada um. Todo mundo que já entrou no
 * sistema aparece aqui: o usuário nasce no primeiro login, como Visualizador.
 */
export const Usuarios: React.FC = () => {
  const { user } = useAuth();
  const { confirm, showSuccess, showError } = useFeedback();
  const [usuarios, setUsuarios] = useState<UsuarioSistema[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);
  const [busca, setBusca] = useState("");

  const carregar = async () => {
    setCarregando(true);
    try {
      setUsuarios(await usuarioService.listar());
    } catch (err: any) {
      showError(
        err?.response?.data?.message ||
          err?.message ||
          "Não foi possível carregar os usuários.",
      );
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const alterarPerfil = async (u: UsuarioSistema, perfil: PerfilUsuario) => {
    if (perfil === u.perfil) return;
    const ok = await confirm({
      title: "Alterar perfil",
      message: `${u.nome || u.email} passa de ${u.perfil} para ${perfil}: ${DESCRICAO_PERFIL[perfil].toLowerCase()}. Vale a partir do próximo acesso ao sistema.`,
      confirmText: "Alterar",
      cancelText: "Cancelar",
    });
    if (!ok) return;

    setSalvandoId(u.id);
    try {
      await usuarioService.alterarPerfil(u.id, perfil);
      setUsuarios((atual) =>
        atual.map((x) => (x.id === u.id ? { ...x, perfil } : x)),
      );
      showSuccess(`Perfil de ${u.nome || u.email} alterado para ${perfil}.`);
    } catch (err: any) {
      // A API explica o motivo: último Admin, próprio perfil, perfil inválido.
      showError(
        err?.response?.data?.message ||
          err?.message ||
          "Não foi possível alterar o perfil.",
      );
    } finally {
      setSalvandoId(null);
    }
  };

  const termo = busca.trim().toLowerCase();
  const filtrados = termo
    ? usuarios.filter(
        (u) =>
          (u.nome || "").toLowerCase().includes(termo) ||
          u.email.toLowerCase().includes(termo),
      )
    : usuarios;

  return (
    <div className="crud-page">
      <div className="page-header">
        <h1>Usuários e permissões</h1>
        <p>
          Quem já entrou no sistema aparece aqui. Quem entra pela primeira vez
          chega como Visualizador.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        {(Object.keys(DESCRICAO_PERFIL) as PerfilUsuario[]).map((p) => (
          <div key={p} className="rounded-lg border border-base-300 p-3 text-sm">
            <div className="font-semibold">{p}</div>
            <div className="opacity-70">{DESCRICAO_PERFIL[p]}</div>
          </div>
        ))}
      </div>

      <input
        type="search"
        className="input input-bordered input-sm w-full md:w-80 mb-3"
        placeholder="Buscar por nome ou e-mail"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
      />

      {carregando ? (
        <div className="flex justify-center py-10">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-base-300">
          <table className="table table-zebra text-sm">
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Último acesso</th>
                <th style={{ width: 220 }}>Perfil</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((u) => {
                const souEu = u.id === user?.id;
                return (
                  <tr key={u.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        {u.avatarUrl ? (
                          <img
                            src={u.avatarUrl}
                            alt=""
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-base-300" />
                        )}
                        <div className="min-w-0">
                          <div className="font-medium">
                            {u.nome || u.email}
                            {souEu && (
                              <span className="badge badge-ghost badge-sm ml-2">
                                você
                              </span>
                            )}
                          </div>
                          <div className="text-xs opacity-70">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="text-xs opacity-80">
                      {u.ultimoAcesso
                        ? formatarTempoRelativo(u.ultimoAcesso)
                        : "—"}
                    </td>
                    <td>
                      <select
                        className="select select-bordered select-sm w-full"
                        value={u.perfil}
                        disabled={souEu || salvandoId === u.id}
                        title={
                          souEu
                            ? "Você não pode alterar o seu próprio perfil"
                            : undefined
                        }
                        onChange={(e) =>
                          alterarPerfil(u, e.target.value as PerfilUsuario)
                        }
                      >
                        <option value="Admin">Admin</option>
                        <option value="Editor">Editor</option>
                        <option value="Visualizador">Visualizador</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
              {filtrados.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center opacity-60 py-6">
                    Nenhum usuário encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
